/**
 * Per-stock-class monthly table.
 * Shows: animal count, weight, allocation %, daily DM, total daily requirement.
 *
 * Label cells use colSpan={2} to merge with the Y-axis spacer column from
 * the parent summary table, keeping column alignment consistent.
 */

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  IconButton,
  InputBase,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import ConfirmDeleteDialog from "features/general/ConfirmDeleteDialog";
import { useCallback, useRef, useState } from "react";
import EditableCell from "./EditableCell";
import {
  BudgetTable,
  LabelCell,
  LabelHeaderCell,
  MonthHeaderCell,
  ValueCell,
} from "./tableStyles";
import type { StockClassComputedMonth, StockClassMonthlyPlan } from "./types";
import { MONTH_LABELS } from "./types";

const noBorder = {
  borderBottom: "none",
  borderTop: "none",
  borderRight: "none",
} as const;

interface Props {
  stockClassPlan: StockClassMonthlyPlan;
  computed: StockClassComputedMonth[];
  visibleMonths?: Set<number>;
  /** Which year's entries to show: 0 = plan year 1 (default), 1 = plan year 2 */
  viewYearOffset?: 0 | 1;
  onUpdate: (
    stockClassId: string,
    month: number,
    yearOffset: 0 | 1,
    field: "animalCount" | "averageWeightKg" | "allocationPercent",
    value: number
  ) => void;
  onUpdateHousing: (
    stockClassId: string,
    month: number,
    yearOffset: 0 | 1,
    isGrazing: boolean
  ) => void;
  onRemove: (stockClassId: string) => void;
  onRename: (stockClassId: string, name: string) => void;
}

export default function StockClassMonthlyTable({
  stockClassPlan,
  computed,
  visibleMonths,
  viewYearOffset = 0,
  onUpdate,
  onUpdateHousing,
  onRemove,
  onRename,
}: Props) {
  const { stockClassId, stockClassName } = stockClassPlan;
  const yearEntries = stockClassPlan.months.filter(
    (m) => (m.yearOffset ?? 0) === viewYearOffset
  );
  const visibleLabels = visibleMonths
    ? MONTH_LABELS.filter((_, i) => visibleMonths.has(i))
    : MONTH_LABELS;
  const visibleEntries = visibleMonths
    ? yearEntries.filter((m) => visibleMonths.has(m.month))
    : yearEntries;
  const visibleComputed = visibleMonths
    ? computed.filter((c) => visibleMonths.has(c.month))
    : computed;
  const colCount = 2 + visibleLabels.length;
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(stockClassName);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleStartEdit = useCallback(() => {
    setNameValue(stockClassName);
    setEditingName(true);
    setTimeout(() => inputRef.current?.select(), 50);
  }, [stockClassName]);

  const handleCommitName = useCallback(() => {
    setEditingName(false);
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== stockClassName) {
      onRename(stockClassId, trimmed);
    }
  }, [nameValue, stockClassName, stockClassId, onRename]);

  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <ConfirmDeleteDialog
        dialogOpen={deleteOpen}
        deletedObjectDescription={stockClassName}
        closeDialog={() => setDeleteOpen(false)}
        onConfirmDelete={() => {
          setDeleteOpen(false);
          onRemove(stockClassId);
        }}
      />
      <BudgetTable size="small">
        <TableHead>
          <TableRow>
            <LabelHeaderCell
              colSpan={2}
              sx={{
                borderBottom: "none",
                minWidth: { xs: 108, sm: 170 },
                width: { xs: 108, sm: 170 },
                px: { xs: 0.5, sm: 1 },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {editingName ? (
                  <InputBase
                    inputRef={inputRef}
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    onBlur={handleCommitName}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCommitName();
                      if (e.key === "Escape") setEditingName(false);
                    }}
                    autoFocus
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "#374151",
                      p: 0,
                      "& input": { p: "2px 4px" },
                      border: "1px solid #d1d5db",
                      borderRadius: 1,
                      backgroundColor: "#fff",
                    }}
                  />
                ) : (
                  <span
                    onClick={handleStartEdit}
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "#374151",
                      cursor: "pointer",
                      padding: "2px 4px",
                      borderRadius: 4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f3f4f6")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    {stockClassName}
                  </span>
                )}
                <IconButton
                  size="small"
                  onClick={() => setDeleteOpen(true)}
                  sx={{
                    opacity: 0.4,
                    "&:hover": { opacity: 1 },
                    p: 0.25,
                    flexShrink: 0,
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>
            </LabelHeaderCell>
            {visibleLabels.map((m) => (
              <MonthHeaderCell
                key={m}
                sx={{
                  borderBottom: "none",
                  px: { xs: 0.75, sm: 1 },
                  width: { xs: 64, sm: 68 },
                }}
              >
                {m}
              </MonthHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <LabelCell
              colSpan={2}
              sx={{
                ...noBorder,
                minWidth: { xs: 108, sm: 170 },
                width: { xs: 108, sm: 170 },
                px: { xs: 0.5, sm: 1.25 },
              }}
            >
              Number of animals
            </LabelCell>
            {visibleEntries.map((entry) => (
              <EditableCell
                key={entry.month}
                value={entry.animalCount}
                onChange={(v) =>
                  onUpdate(
                    stockClassId,
                    entry.month,
                    viewYearOffset,
                    "animalCount",
                    v
                  )
                }
                decimals={0}
                cellSx={noBorder}
              />
            ))}
          </TableRow>

          <TableRow>
            <LabelCell
              colSpan={2}
              sx={{
                ...noBorder,
                minWidth: { xs: 108, sm: 170 },
                width: { xs: 108, sm: 170 },
                px: { xs: 0.5, sm: 1.25 },
              }}
            >
              Average weight (kg)
            </LabelCell>
            {visibleEntries.map((entry) => (
              <EditableCell
                key={entry.month}
                value={entry.averageWeightKg}
                onChange={(v) =>
                  onUpdate(
                    stockClassId,
                    entry.month,
                    viewYearOffset,
                    "averageWeightKg",
                    v
                  )
                }
                decimals={0}
                step={5}
                cellSx={noBorder}
              />
            ))}
          </TableRow>

          <TableRow>
            <LabelCell
              colSpan={2}
              sx={{
                ...noBorder,
                minWidth: { xs: 108, sm: 170 },
                width: { xs: 108, sm: 170 },
                px: { xs: 0.5, sm: 1.25 },
              }}
            >
              Allocation (% of body wt.)
            </LabelCell>
            {visibleEntries.map((entry) => (
              <EditableCell
                key={entry.month}
                value={entry.allocationPercent}
                onChange={(v) =>
                  onUpdate(
                    stockClassId,
                    entry.month,
                    viewYearOffset,
                    "allocationPercent",
                    v
                  )
                }
                decimals={1}
                max={10}
                cellSx={noBorder}
              />
            ))}
          </TableRow>

          {/* Spacer before computed rows */}
          <TableRow>
            <TableCell
              colSpan={colCount}
              sx={{ p: 0, height: 8, border: "none" }}
            />
          </TableRow>

          <TableRow>
            <LabelCell
              colSpan={2}
              sx={{
                ...noBorder,
                fontWeight: 700,
                minWidth: { xs: 108, sm: 170 },
                width: { xs: 108, sm: 170 },
                px: { xs: 0.5, sm: 1.25 },
              }}
            >
              Daily DM per head (kg)
            </LabelCell>
            {visibleComputed.map((c) => (
              <Tooltip
                key={c.month}
                title="Weight × Allocation %"
                arrow
                placement="top"
              >
                <ValueCell
                  sx={{
                    ...noBorder,
                    fontWeight: 700,
                    px: { xs: 0.75, sm: 1 },
                    width: { xs: 64, sm: 68 },
                  }}
                >
                  {Math.round(c.dailyAllocationKgDM)}
                </ValueCell>
              </Tooltip>
            ))}
          </TableRow>

          <TableRow>
            <LabelCell
              colSpan={2}
              sx={{
                ...noBorder,
                fontWeight: 700,
                minWidth: { xs: 108, sm: 170 },
                width: { xs: 108, sm: 170 },
                px: { xs: 0.5, sm: 1.25 },
              }}
            >
              Total daily req. (kg DM)
            </LabelCell>
            {visibleComputed.map((c) => (
              <Tooltip
                key={c.month}
                title="Daily DM × Number of animals"
                arrow
                placement="top"
              >
                <ValueCell
                  sx={{
                    ...noBorder,
                    fontWeight: 700,
                    px: { xs: 0.75, sm: 1 },
                    width: { xs: 64, sm: 68 },
                  }}
                >
                  {Math.round(c.totalDailyRequirementKgDM).toLocaleString()}
                </ValueCell>
              </Tooltip>
            ))}
          </TableRow>
        </TableBody>
      </BudgetTable>
    </TableContainer>
  );
}
